import {
    Badge,
    Button,
    Flex,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Table as ChakraTable,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { AgentNewsType } from '@olagg/db-types';
import Pagination from '@olagg/ui-kit/Pagination';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { BsSearch } from 'react-icons/bs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../consts/routes';
import useAgentNews from '../hooks/useAgentNews';

const List = () => {
    const [newsList, setNewsList] = useState<AgentNewsType[]>([]);
    const [count, setCount] = useState(0);
    const { getAll } = useAgentNews();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [filter, setFilter] = useState({
        title: searchParams.get('title') || '',
        status: (searchParams.get('status') || '') as '' | 'draft' | 'published',
        ai_generated: (searchParams.get('ai_generated') || '') as '' | 'true' | 'false',
        page: Number(searchParams.get('page') || 1),
        limit: Number(searchParams.get('limit') || 10),
    });

    const { page, limit, title, status, ai_generated } = filter;

    const loadNews = () =>
        getAll({ title: title || undefined, status: status || undefined, ai_generated: ai_generated !== '' ? ai_generated === 'true' : undefined, page, limit }).then((response) => {
            setNewsList(response.news);
            setCount(response.count);
        });

    const onFilter = ({ name, value }: EventTarget & HTMLInputElement) => {
        setFilter((prev) => ({ ...prev, [name]: value, page: name !== 'page' ? 1 : Number(value) }));

        if (value) searchParams.set(name, value);
        else searchParams.delete(name);
        if (name !== 'page') searchParams.delete('page');
        setSearchParams(searchParams);
    };

    useEffect(() => {
        loadNews();
    }, [filter]);

    return (
        <Flex gap={5} direction="column" mt={4}>
            <Flex flexDirection="row" justify="space-between" pr={6} gap={2} mx={5}>
                <InputGroup w="40%">
                    <Input
                        color="white"
                        placeholder="Buscar por título"
                        name="title"
                        value={title}
                        onChange={(e) => onFilter(e.target)}
                    />
                    <InputRightElement children={<BsSearch color="white" />} />
                </InputGroup>
                <Flex gap={2}>
                    <Select
                        color="white"
                        w="200px"
                        name="status"
                        value={status}
                        onChange={(e) => onFilter(e.target as any)}
                        sx={{ option: { color: 'black' } }}
                    >
                        <option value="">Todos los estados</option>
                        <option value="draft">Borrador</option>
                        <option value="published">Publicado</option>
                    </Select>
                    <Select
                        color="white"
                        w="200px"
                        name="ai_generated"
                        value={ai_generated}
                        onChange={(e) => onFilter(e.target as any)}
                        sx={{ option: { color: 'black' } }}
                    >
                        <option value="">Todos los orígenes</option>
                        <option value="true">Generado por IA</option>
                        <option value="false">Creado manualmente</option>
                    </Select>
                </Flex>
            </Flex>

            <Flex w="full" justifyContent="center" px="20px" pb="15px">
                <TableContainer overflowX="scroll" overflowY="scroll" w="100%" maxH="60vh">
                    <ChakraTable
                        variant="striped"
                        size="sm"
                        colorScheme="olaggPurple"
                        fontSize="18px"
                        position="relative"
                        whiteSpace="normal"
                    >
                        <Thead>
                            <Tr position="sticky" bg="gray.800" top="0" zIndex="100">
                                <Th py="20px" color="white">ID</Th>
                                <Th py="20px" color="white">Título</Th>
                                <Th py="20px" color="white">Categoría</Th>
                                <Th py="20px" color="white">Estado</Th>
                                <Th py="20px" color="white">Creado</Th>
                                <Th py="20px" color="white">Acciones</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {newsList?.map((item, index) => (
                                <Tr key={index} color="white">
                                    <Td py="20px">{item.id}</Td>
                                    <Td py="20px" maxW="300px">{item.title}</Td>
                                    <Td py="20px">{item.main_category || item.category?.name || '-'}</Td>
                                    <Td py="20px">
                                        <Badge colorScheme={item.status === 'published' ? 'green' : 'yellow'}>
                                            {item.status === 'published' ? 'Publicado' : 'Borrador'}
                                        </Badge>
                                    </Td>
                                    <Td py="20px">{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</Td>
                                    <Td py="20px">
                                        <Button
                                            bgColor="#37A169"
                                            p="0 10px"
                                            h="30px"
                                            fontSize="14px"
                                            onClick={() => navigate(`/agent-news/${item.id}`)}
                                        >
                                            {item.status === 'draft' ? 'Revisar' : 'Ver'}
                                        </Button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </ChakraTable>
                </TableContainer>
            </Flex>

            <Flex px="20px" w="full" justifyContent="center">
                <Pagination
                    params={{ limit, page, count }}
                    onClick={(p) =>
                        onFilter({ name: 'page', value: String(p + 1) } as EventTarget & HTMLInputElement)
                    }
                    onLimitChange={(val) =>
                        onFilter({ name: 'limit', value: val } as EventTarget & HTMLInputElement)
                    }
                    hideLimitInput
                />
            </Flex>
        </Flex>
    );
};

export default List;
